import { AnimatePresence, motion } from "framer-motion";
import { Check, X, Zap, Building2, Sparkles } from "lucide-react";
import { useUpgrade } from "../UpgradeContext";
import { ProgressBar } from "./ui";

const PLAN_CARDS = [
  {
    key: "pro", name: "Pro", price: 29, icon: Zap, popular: true,
    benefits: ["Unlimited AI Credit Coach", "Unlimited report analysis & uploads", "Unlimited AI dispute letters", "2 GB encrypted Document Vault", "Advanced analytics", "Priority email support"],
  },
  {
    key: "business", name: "Business", price: 79, icon: Building2, popular: false,
    benefits: ["Everything in Pro", "Multiple businesses & profiles", "Team collaboration & access", "Unlimited document storage", "Advanced business reports", "Priority support"],
  },
];

const fmt = (r) => `${r.used}${r.unit ? " " + r.unit : ""} / ${r.limit}${r.unit ? " " + r.unit : ""}`;

export const UpgradeModal = () => {
  const { modal, metrics, closeModal, startCheckout } = useUpgrade();
  const hitMetric = metrics.find((m) => m.key === modal.metric);
  const heading = hitMetric && hitMetric.pct >= 100
    ? `You've reached your ${hitMetric.label.toLowerCase()} limit`
    : hitMetric
    ? `You're approaching your ${hitMetric.label.toLowerCase()} limit`
    : "Unlock the full ArcaCentum Credit experience";

  return (
    <AnimatePresence>
      {modal.open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="upgrade-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeModal} />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto cc-scroll cc-card rounded-2xl p-6 sm:p-8"
          >
            <button
              onClick={closeModal}
              data-testid="upgrade-modal-close"
              aria-label="Close"
              className="absolute right-4 top-4 text-slate-400 hover:text-white p-1"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-2.5 mb-1">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg cc-gradient text-white"><Sparkles size={16} /></span>
              <span className="text-xs tracking-[0.2em] uppercase text-blue-300">Upgrade</span>
            </div>
            <h2 className="cc-heading text-2xl sm:text-3xl font-700 text-white tracking-tight">{heading}</h2>
            <p className="text-slate-400 text-sm mt-2">
              You're on the Free plan. Here's where you stand this month — upgrade for unlimited access.
            </p>

            {/* Current usage */}
            {metrics.length > 0 && (
              <div className="mt-5 space-y-3 rounded-xl cc-surface border border-white/[0.06] p-4" data-testid="upgrade-usage">
                {metrics.map((m) => (
                  <div key={m.key} data-testid={`usage-row-${m.key}`}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-slate-300">{m.label}</span>
                      <span className={m.pct >= 100 ? "text-red-300" : "text-slate-400"}>
                        {fmt(m)} · {m.remaining} left
                      </span>
                    </div>
                    <ProgressBar value={m.pct} />
                  </div>
                ))}
              </div>
            )}

            {/* Plan cards */}
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {PLAN_CARDS.map((p) => (
                <div
                  key={p.key}
                  className={`rounded-xl p-5 flex flex-col cc-surface border ${p.popular ? "border-blue-400/40 ring-1 ring-blue-500/30" : "border-white/[0.08]"}`}
                  data-testid={`upgrade-card-${p.key}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg cc-surface-2 text-blue-300"><p.icon size={16} /></span>
                      <span className="cc-heading text-lg font-600 text-white">{p.name}</span>
                    </div>
                    {p.popular && <span className="rounded-full cc-gradient px-2.5 py-0.5 text-[10px] font-500 text-white">Popular</span>}
                  </div>
                  <div className="mt-3 cc-heading text-3xl font-700 text-white">
                    ${p.price}<span className="text-slate-500 text-sm font-400">/mo</span>
                  </div>
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-xs text-slate-300">
                        <Check size={13} className="mt-0.5 shrink-0 text-blue-400" />{b}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => startCheckout(p.key, modal.metric)}
                    data-testid={`upgrade-${p.key}-btn`}
                    className={`mt-5 rounded-xl py-2.5 text-sm font-500 ${p.popular ? "cc-btn-primary text-white" : "border border-white/12 text-white cc-nav-item"}`}
                  >
                    Upgrade to {p.name}
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-center justify-between">
              <button
                onClick={closeModal}
                data-testid="upgrade-modal-dismiss"
                className="text-sm text-slate-400 hover:text-white"
              >
                Maybe later
              </button>
              <span className="text-xs text-slate-500">Secure checkout by Stripe · Cancel anytime</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
